import { useCallback, useEffect, useState } from 'react'
import { getAllProducts } from '@/api/products'
import { updateTransparentPrintBackground } from '@/api/productPrintSettings'
import { toPrintProductRows, type PrintProductRow } from './printProductRows'

const LOAD_ERROR = 'The products could not be loaded.'
const SAVE_ERROR = 'The change was not saved. Try again.'

/**
 * The Transparent Print page's state: the product rows, loaded once `enabled`
 * (the caller is a super admin), and a toggle that shows the new value at
 * once and puts the old one back if the save fails. A product's toggle is
 * refused while its save is pending (`pendingIds`), so two saves of one
 * product never race and a failed one never rolls back over a later one.
 */
export const useTransparentPrintBackgroundProducts = (enabled: boolean) => {
  const [rows, setRows] = useState<PrintProductRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  )

  useEffect(() => {
    if (!enabled) return
    let current = true
    getAllProducts()
      .then(({ data }) => {
        if (current) setRows(toPrintProductRows(data))
      })
      .catch(() => {
        if (current) setError(LOAD_ERROR)
      })
    return () => {
      current = false
    }
  }, [enabled])

  const setRowEnabled = useCallback(
    (target: PrintProductRow, value: boolean) =>
      setRows(
        (previous) =>
          previous?.map((row) =>
            row.id === target.id ? { ...row, enabled: value } : row,
          ) ?? previous,
      ),
    [],
  )

  const setPending = useCallback(
    (id: string, pending: boolean) =>
      setPendingIds((previous) => {
        const next = new Set(previous)
        if (pending) next.add(id)
        else next.delete(id)
        return next
      }),
    [],
  )

  const toggle = useCallback(
    async (row: PrintProductRow) => {
      if (pendingIds.has(row.id)) return
      const next = !row.enabled
      setError(null)
      setPending(row.id, true)
      setRowEnabled(row, next)
      try {
        await updateTransparentPrintBackground(row.id, next)
      } catch {
        setRowEnabled(row, row.enabled)
        setError(SAVE_ERROR)
      } finally {
        setPending(row.id, false)
      }
    },
    [pendingIds, setPending, setRowEnabled],
  )

  return { rows, error, toggle, pendingIds }
}
